import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Invoice from '../../entities/invoice';
import Expense from '../../entities/expense';
import FlatFee from '../../entities/flatFee';
import FlatFeeMilestone from '../../entities/flatFeeMilestone';
import InvoiceBatch from '../../entities/invoiceBatch';
import InvoiceBatchItem from '../../entities/invoiceBatchItem';
import Task from '../../entities/task';
import Transaction from '../../entities/transaction';
import Discount from '../../entities/discount';
import Document from '../../entities/document';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteDiscount, collectDiscountDeleteImpact} from './discountDeps';
import {deleteDocument, collectDocumentDeleteImpact} from './documentDeps';
export function canDeleteInvoice(): boolean {
return canCascadeDelete();
}
export function collectInvoiceDeleteImpact(invoice: Invoice, context: DeleteImpactContext): void {
const key = 'invoice:' + invoice.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const expenseInvoiceIdImpact = workingRows<Expense>('expense', findAll<Expense>('expenses')).filter(expenseRef => expenseRef.invoiceId === invoice.id);
expenseInvoiceIdImpact.forEach(expenseRef => addDeleteImpact(context, 'expense:' + expenseRef.id, 'expenses'));
const flatFeeInvoiceIdImpact = workingRows<FlatFee>('flatFee', findAll<FlatFee>('flatFees')).filter(flatFeeRef => flatFeeRef.invoiceId === invoice.id);
flatFeeInvoiceIdImpact.forEach(flatFeeRef => addDeleteImpact(context, 'flatFee:' + flatFeeRef.id, 'flatFees'));
const flatFeeMilestoneInvoiceIdImpact = workingRows<FlatFeeMilestone>('flatFeeMilestone', findAll<FlatFeeMilestone>('flatFeeMilestones')).filter(flatFeeMilestoneRef => flatFeeMilestoneRef.invoiceId === invoice.id);
flatFeeMilestoneInvoiceIdImpact.forEach(flatFeeMilestoneRef => addDeleteImpact(context, 'flatFeeMilestone:' + flatFeeMilestoneRef.id, 'flatFeeMilestones'));
const invoiceBatchInvoiceIdsImpact = workingRows<InvoiceBatch>('invoiceBatch', findAll<InvoiceBatch>('invoiceBatches')).filter(invoiceBatchRef => invoiceBatchRef.invoiceIds?.includes(invoice.id));
invoiceBatchInvoiceIdsImpact.forEach(invoiceBatchRef => addDeleteImpact(context, 'invoiceBatch:' + invoiceBatchRef.id, 'invoiceBatches'));
const invoiceBatchItemInvoiceIdImpact = workingRows<InvoiceBatchItem>('invoiceBatchItem', findAll<InvoiceBatchItem>('invoiceBatchItems')).filter(invoiceBatchItemRef => invoiceBatchItemRef.invoiceId === invoice.id);
invoiceBatchItemInvoiceIdImpact.forEach(invoiceBatchItemRef => addDeleteImpact(context, 'invoiceBatchItem:' + invoiceBatchItemRef.id, 'invoiceBatchItems'));
const taskInvoiceIdImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.invoiceId === invoice.id);
taskInvoiceIdImpact.forEach(taskRef => addDeleteImpact(context, 'task:' + taskRef.id, 'tasks'));
const transactionInvoiceIdImpact = workingRows<Transaction>('transaction', findAll<Transaction>('transactions')).filter(transactionRef => transactionRef.invoiceId === invoice.id && !(findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(transactionRef.id) || invoice.id === transactionRef.invoiceId)).length > 0));
transactionInvoiceIdImpact.forEach(transactionRef => addDeleteImpact(context, 'transaction:' + transactionRef.id, 'transactions'));workingRows<Discount>('discount', findAll<Discount>('discounts')).filter(discount => discount.status !== 'D' && discount.entityType === 'invoice' && discount.entityId === invoice.id).forEach(discount => {
if (context.visited.has('discount:' + discount.id)) return;
addDeleteImpact(context, 'discount:' + discount.id, 'discounts');
collectDiscountDeleteImpact(discount, context);
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'invoice' && document.entityId === invoice.id).forEach(document => {
if (context.visited.has('document:' + document.id)) return;
addDeleteImpact(context, 'document:' + document.id, 'documents');
collectDocumentDeleteImpact(document, context);
});
}
export function deleteImpactInvoice(invoice: Invoice): DeleteImpact {
const context = deleteImpactContext();
collectInvoiceDeleteImpact(invoice, context);
return deleteImpactCounts(context);
}
export function deleteInvoice(invoice: Invoice, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteInvoice()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectInvoiceDeleteImpact(invoice, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || invoice.id};
}
const key = 'invoice:' + invoice.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'invoice' && document.entityId === invoice.id).forEach(document => {deleteDocument(document, mergeId, cascade);});

const deleted = invoice.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.InvoiceActions.removeInvoiceQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceInvoice(invoice: Invoice, replaceWith: Invoice, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = invoice.id;}
const _work = workingSet();
workingRows<Expense>('expense', [...store.getState().expenses.getMappingFor('invoiceId', invoice.id)]).filter(expenseTemp => expenseTemp.invoiceId === invoice.id).forEach(expenseTemp => {
const _expense = ((_work.get('expense:' + expenseTemp.id) ?? expenseTemp) as Expense).clone();
if (_expense.status === 'D') return;
_expense.status = 'U';
_expense.addedOn = newHRTimestamp();
_expense.addedBy = getCurrentUserId();
_expense.importSource = targetImportSource;
_expense.importId = mergeId;
if (_expense.invoiceId === invoice.id) {
_expense.invoiceId = replaceWith.id;

}
_work.set('expense:' + _expense.id, _expense);
store.dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense));
});
workingRows<FlatFee>('flatFee', [...store.getState().flatFees.getMappingFor('invoiceId', invoice.id)]).filter(flatFeeTemp => flatFeeTemp.invoiceId === invoice.id).forEach(flatFeeTemp => {
const _flatFee = ((_work.get('flatFee:' + flatFeeTemp.id) ?? flatFeeTemp) as FlatFee).clone();
if (_flatFee.status === 'D') return;
_flatFee.status = 'U';
_flatFee.addedOn = newHRTimestamp();
_flatFee.addedBy = getCurrentUserId();
_flatFee.importSource = targetImportSource;
_flatFee.importId = mergeId;
if (_flatFee.invoiceId === invoice.id) {
_flatFee.invoiceId = replaceWith.id;

}
_work.set('flatFee:' + _flatFee.id, _flatFee);
store.dispatch(Actions.FlatFeeActions.saveFlatFeeQueue(_flatFee));
});
workingRows<FlatFeeMilestone>('flatFeeMilestone', [...store.getState().flatFeeMilestones.getMappingFor('invoiceId', invoice.id)]).filter(flatFeeMilestoneTemp => flatFeeMilestoneTemp.invoiceId === invoice.id).forEach(flatFeeMilestoneTemp => {
const _flatFeeMilestone = ((_work.get('flatFeeMilestone:' + flatFeeMilestoneTemp.id) ?? flatFeeMilestoneTemp) as FlatFeeMilestone).clone();
if (_flatFeeMilestone.status === 'D') return;
_flatFeeMilestone.status = 'U';
_flatFeeMilestone.addedOn = newHRTimestamp();
_flatFeeMilestone.addedBy = getCurrentUserId();
_flatFeeMilestone.importSource = targetImportSource;
_flatFeeMilestone.importId = mergeId;
if (_flatFeeMilestone.invoiceId === invoice.id) {
_flatFeeMilestone.invoiceId = replaceWith.id;

}
_work.set('flatFeeMilestone:' + _flatFeeMilestone.id, _flatFeeMilestone);
store.dispatch(Actions.FlatFeeMilestoneActions.saveFlatFeeMilestoneQueue(_flatFeeMilestone));
});
workingRows<InvoiceBatch>('invoiceBatch', [...store.getState().invoiceBatches.getMappingFor('invoiceId', invoice.id)]).filter(invoiceBatchTemp => invoiceBatchTemp.invoiceIds?.includes(invoice.id)).forEach(invoiceBatchTemp => {
const _invoiceBatch = ((_work.get('invoiceBatch:' + invoiceBatchTemp.id) ?? invoiceBatchTemp) as InvoiceBatch).clone();
if (_invoiceBatch.status === 'D') return;
_invoiceBatch.status = 'U';
_invoiceBatch.addedOn = newHRTimestamp();
_invoiceBatch.addedBy = getCurrentUserId();
_invoiceBatch.importSource = targetImportSource;
_invoiceBatch.importId = mergeId;
_invoiceBatch.invoiceIds = _invoiceBatch.invoiceIds?.map(id => (id === invoice.id) ? replaceWith.id : id) || [];

_work.set('invoiceBatch:' + _invoiceBatch.id, _invoiceBatch);
store.dispatch(Actions.InvoiceBatchActions.saveInvoiceBatchQueue(_invoiceBatch));
});
workingRows<InvoiceBatchItem>('invoiceBatchItem', [...store.getState().invoiceBatchItems.getMappingFor('invoiceId', invoice.id)]).filter(invoiceBatchItemTemp => invoiceBatchItemTemp.invoiceId === invoice.id).forEach(invoiceBatchItemTemp => {
const _invoiceBatchItem = ((_work.get('invoiceBatchItem:' + invoiceBatchItemTemp.id) ?? invoiceBatchItemTemp) as InvoiceBatchItem).clone();
if (_invoiceBatchItem.status === 'D') return;
_invoiceBatchItem.status = 'U';
_invoiceBatchItem.addedOn = newHRTimestamp();
_invoiceBatchItem.addedBy = getCurrentUserId();
_invoiceBatchItem.importSource = targetImportSource;
_invoiceBatchItem.importId = mergeId;
if (_invoiceBatchItem.invoiceId === invoice.id) {
_invoiceBatchItem.invoiceId = replaceWith.id;

}
_work.set('invoiceBatchItem:' + _invoiceBatchItem.id, _invoiceBatchItem);
store.dispatch(Actions.InvoiceBatchItemActions.saveInvoiceBatchItemQueue(_invoiceBatchItem));
});
workingRows<Task>('task', [...store.getState().tasks.getMappingFor('invoiceId', invoice.id)]).filter(taskTemp => taskTemp.invoiceId === invoice.id).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
if (_task.invoiceId === invoice.id) {
_task.invoiceId = replaceWith.id;

}
_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});
workingRows<Transaction>('transaction', [...store.getState().transactions.getMappingFor('invoiceId', invoice.id)]).filter(transactionTemp => transactionTemp.invoiceId === invoice.id).forEach(transactionTemp => {
const _transaction = ((_work.get('transaction:' + transactionTemp.id) ?? transactionTemp) as Transaction).clone();
if (_transaction.status === 'D') return;
if (findAll<Invoice>('invoices').filter(invoice => invoice.invoiceStatus === 'F' && (invoice.transactionIds?.includes(_transaction.id) || invoice.id === _transaction.invoiceId)).length > 0) return;
_transaction.status = 'U';
_transaction.addedOn = newHRTimestamp();
_transaction.addedBy = getCurrentUserId();
_transaction.importSource = targetImportSource;
_transaction.importId = mergeId;
if (_transaction.invoiceId === invoice.id) {
_transaction.invoiceId = replaceWith.id;

}
_work.set('transaction:' + _transaction.id, _transaction);
store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
});



workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'invoice' && document.entityId === invoice.id).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.entityId = replaceWith.id;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
return invoice;
}
